// src/components/store/sections/blog-posts/DynamicBlogVariant.tsx
"use client";

import React, { useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { ArrowRight, ChevronLeft, ChevronRight, Sparkles, BookOpen, Calendar, User } from "lucide-react";

const FALLBACK_BLOGS = [
  {
    id: 1,
    title: "10 Essential Wardrobe Staples for the Modern Season",
    slug: "wardrobe-staples",
    category_tag: "Style Guide",
    author_name: "Ariana Rahman",
    published_at: "18 Oct 2026",
    summary: "Discover how to build a versatile capsule wardrobe with breathable fabrics and timeless silhouettes.",
    cover_image: "https://images.unsplash.com/photo-1483985988355-763728e1935b?w=600&auto=format&fit=crop&q=80",
  },
  {
    id: 2,
    title: "Behind the Craft: How Authentic Materials Make the Difference",
    slug: "behind-the-craft",
    category_tag: "Craftsmanship",
    author_name: "Tanvir Hasan",
    published_at: "15 Oct 2026",
    summary: "A closer look at sustainable sourcing, artisan stitching, and quality control in every piece we ship.",
    cover_image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600&auto=format&fit=crop&q=80",
  },
  {
    id: 3,
    title: "Smart Home & Everyday Tech Gadgets Worth Upgrading To",
    slug: "smart-home-gadgets",
    category_tag: "Tech & Living",
    author_name: "Nadia Islam",
    published_at: "11 Oct 2026",
    summary: "Upgrade your daily routine with minimalist smart accessories designed for speed and durability.",
    cover_image: "https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=600&auto=format&fit=crop&q=80",
  },
  {
    id: 4,
    title: "Organic Skincare Rituals for Naturally Glowing Skin",
    slug: "organic-skincare-rituals",
    category_tag: "Wellness",
    author_name: "Samira Khan",
    published_at: "08 Oct 2026",
    summary: "Botanical extracts and cold-pressed serums that nourish your skin barrier without harsh chemicals.",
    cover_image: "https://images.unsplash.com/photo-1522335789203-aabd1fc54bc9?w=600&auto=format&fit=crop&q=80",
  },
];

export default function DynamicBlogVariant({
  variantNumber,
  archKey,
  archLabel,
  palName,
  grad,
  lightBg,
  textCol,
  borderCol,
  btnCls,
  blogs = [],
}: any) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const items = Array.isArray(blogs) && blogs.length > 0 ? blogs : FALLBACK_BLOGS;
  const isDark = archKey === "cyber_feed" || palName === "SlateGold";

  const scroll = (dir: "left" | "right") => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir === "left" ? -340 : 340, behavior: "smooth" });
    }
  };

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-6 select-none font-sans space-y-6">
      <div className="flex items-center justify-between border-b border-slate-200/80 pb-4">
        <div>
          <span className={`text-xs font-black uppercase tracking-wider flex items-center gap-1.5 font-mono ${textCol}`}>
            <BookOpen className="w-3.5 h-3.5" /> Variant {variantNumber}: {archLabel} ({palName})
          </span>
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight mt-0.5">
            Latest News & Store Blogs
          </h2>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => scroll("left")}
            className={`w-8 h-8 rounded-full border-2 ${borderCol} bg-white text-slate-700 flex items-center justify-center shadow-xs cursor-pointer active:scale-95`}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => scroll("right")}
            className={`w-8 h-8 rounded-full border-2 ${borderCol} bg-white text-slate-700 flex items-center justify-center shadow-xs cursor-pointer active:scale-95`}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex items-stretch gap-5 overflow-x-auto pb-4 pt-1 px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden scroll-smooth"
      >
        {items.map((post: any, idx: number) => {
          const img = post.cover_image || post.image || FALLBACK_BLOGS[idx % FALLBACK_BLOGS.length].cover_image;
          const imgUrl = typeof img === "string" && (img.startsWith("http") || img.startsWith("/")) ? img : FALLBACK_BLOGS[0].cover_image;
          const title = typeof post.title === "string" ? post.title : "Featured Store Article";
          const tag = typeof post.category_tag === "string" ? post.category_tag : archLabel;
          const author = typeof post.author_name === "string" ? post.author_name : "Editorial Team";

          return (
            <article
              key={post.id || idx}
              className={`w-72 sm:w-80 shrink-0 ${
                archKey === "cathedral_arch"
                  ? "rounded-t-[75px] rounded-b-3xl"
                  : archKey === "polaroid_stories"
                  ? "rounded-2xl p-3 bg-[#fffdf9] -rotate-1 hover:rotate-0"
                  : "rounded-3xl"
              } ${
                isDark ? "bg-slate-950 text-white border-slate-800" : `bg-white text-slate-900 ${borderCol}`
              } border-2 shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col justify-between group`}
            >
              <div>
                <div className="relative h-44 w-full overflow-hidden bg-slate-100">
                  <Image src={imgUrl} alt={title} fill unoptimized className="object-cover group-hover:scale-105 transition-transform duration-500" />
                  <span className={`absolute top-3 left-3 px-3 py-1 rounded-full bg-gradient-to-r ${grad} text-white text-[10px] font-black uppercase tracking-wider shadow-xs`}>
                    {tag}
                  </span>
                </div>
                <div className="p-4 space-y-2 text-left">
                  <div className="flex items-center gap-3 text-[10px] font-bold text-slate-400">
                    <span className="flex items-center gap-1"><User className="w-3 h-3" /> {author}</span>
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Oct 2026</span>
                  </div>
                  <h3 className="text-sm font-black leading-snug line-clamp-2 group-hover:underline">
                    {title}
                  </h3>
                  <p className={`text-xs line-clamp-2 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                    {post.summary || "Read our curated guide and insider tips from our store specialists."}
                  </p>
                </div>
              </div>
              <div className="px-4 pb-4 pt-2 flex items-center justify-between border-t border-slate-100/80">
                <span className="text-[10px] font-mono font-bold text-slate-400">3 MIN READ</span>
                <Link
                  href={`/blogs/${post.slug || post.id || 1}`}
                  className={`px-3.5 py-1.5 rounded-xl bg-gradient-to-r ${grad} text-white text-[10px] font-black uppercase tracking-wider inline-flex items-center gap-1`}
                >
                  <span>Read Story</span>
                  <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
